import { Component } from "react"

/* Constants */
import { sound, utils } from "../constants"

/* Components */
import { Chip3D } from "@cuba-go/poker-ui"

/* Status */
import { CHOICE } from "../constants/status"


/* Spot labels + text sizes (ported from eb1-front Rates) */
const LABELS = { bustBonus: "BUST BONUS", twentyone: "21+3", main: "BET", perfectPair: "PERFECT PAIRS", crazySeven: "CRAZY 7" }
const SIZES = { bustBonus: 12, twentyone: 16, main: 15, perfectPair: 11, crazySeven: 12 }

/* Side-bet spots (all but the centre BET) */
const SIDE_BETS = ["bustBonus", "twentyone", "perfectPair", "crazySeven"]

/* Mobile breakpoint — must match the CSS @media (orientation: portrait) rule */
const MOBILE_MQ = "(orientation: portrait)"

/* Branded line-art icons (Pragmatic ONE Blackjack SVGs) for the bet spots,
   served from public/images/spots/. Each side bet falls back to its text
   label if its file is missing (see _icon onError). The centre BET spot
   uses the "ghost chip" ring (_betRing). */
const ICONS = {
    bustBonus: `${process.env.PUBLIC_URL}/images/spots/bust-bonus.svg`,
    twentyone: `${process.env.PUBLIC_URL}/images/spots/twentyone.svg`,
    perfectPair: `${process.env.PUBLIC_URL}/images/spots/perfect-pairs.svg`,
    crazySeven: `${process.env.PUBLIC_URL}/images/spots/crazy-7.svg`,
    main: `${process.env.PUBLIC_URL}/images/spots/bet-ring.svg`,
}


/*
    Bets — ONE BLACKJACK bet spots, ported from the eb1-front Rates widget:
    amber round spots in an arc (BET centre, side bets raised), with chip
    placement and win-multiplier animations. Wired to the live stake model.

    Props: status, stake, setStake, selected, chips, list, setList,
           balance, max, notify, result
*/
class Bets extends Component {

    /* Track which spot icons failed to load -> fall back to the text label.
       isMobile mirrors the CSS portrait breakpoint so Chip3D (a JS px size,
       not stylable via CSS) can use different sizes on mobile vs desktop. */
    state = {
        iconFail: {},
        isMobile: typeof window !== "undefined" && window.matchMedia(MOBILE_MQ).matches,
    }

    componentDidMount() {
        this._mq = window.matchMedia(MOBILE_MQ)
        this._onMq = e => this.setState({ isMobile: e.matches })
        if (this._mq.addEventListener) this._mq.addEventListener("change", this._onMq)
        else this._mq.addListener(this._onMq)
    }

    componentWillUnmount() {
        if (!this._mq) return
        if (this._mq.removeEventListener) this._mq.removeEventListener("change", this._onMq)
        else this._mq.removeListener(this._onMq)
    }

    /* Chip pixel size — bigger on desktop, smaller on mobile (portrait).
       Tweak these four numbers to control PC / mobile chip sizes. */
    _chipSize = (isMain = false) => {
        const { isMobile } = this.state
        if (isMain) return isMobile ? 68 : 90
        return isMobile ? 56 : 70
    }

    /* Branded icon for a side-bet spot (falls back to text if missing) */
    _icon = target => {
        const src = ICONS[target]
        if (!src || this.state.iconFail[target]) return null
        return (
            <img
                className="game-rate-icon"
                src={src}
                alt={LABELS[target]}
                onError={() => this.setState(s => ({ iconFail: { ...s.iconFail, [target]: true } }))}
            />
        )
    }

    /* "Ghost chip" ring for the empty centre BET spot */
    _betRing = () => (
        <img className="game-rate-ring" src={ICONS.main} alt="BET" aria-hidden="true" />
    )

    /* Chip denomination values (ascending) — Chip3D derives its colour from
       the value's tier among these, so this MUST be passed (a `color` prop is
       ignored by Chip3D). */
    _chipValues = () => (this.props.chips || []).map(c => c.value)

    notify = type => {
        const { notify } = this.props
        notify(type, "show")
        const timer = setTimeout(() => { notify(type, "close"); clearTimeout(timer) }, 2000)
    }

    /* Place the selected chip on a spot */
    add = target => {

        const { status, stake, selected, chips, setStake, list, setList, balance, max } = this.props

        if (status !== CHOICE || !chips[selected] || !target) return

        /* Side bets are locked until a main BET is placed */
        if (target !== "main" && !(parseFloat(stake.main) > 0)) {
            sound.play("blocked", 0.3)
            return
        }

        const value = chips[selected].value
        const _stake = { ...stake }
        const _list = [...list]
        const cap = utils.betMax(target, max)

        if (value > parseFloat(balance)) {
            this.notify("balanceError")
            sound.play("blocked", 0.3)
            return
        }

        const next = (_stake[target] || 0) + value

        if (next > cap) {
            this.notify("maxBetError")
            sound.play("blocked", 0.3)
            return
        }

        _stake[target] = next
        _list.push({ value, type: "add", target })

        setStake(_stake)
        setList(_list)
        sound.play("sound", 0.2)
    }

    /* Place the selected chip on all four side bets at once (ALL SIDE BETS) */
    _addAll = () => {

        const { status, stake, selected, chips, setStake, list, setList, balance, max } = this.props

        if (status !== CHOICE || !chips[selected]) return

        /* Requires a main BET first, same rule as the individual side spots */
        if (!(parseFloat(stake.main) > 0)) {
            sound.play("blocked", 0.3)
            return
        }

        const value = chips[selected].value

        if (value > parseFloat(balance)) {
            this.notify("balanceError")
            sound.play("blocked", 0.3)
            return
        }

        const _stake = { ...stake }
        const _list = [...list]
        let added = 0

        SIDE_BETS.forEach(target => {
            const cap = utils.betMax(target, max)
            const next = (_stake[target] || 0) + value
            if (next <= cap) {
                _stake[target] = next
                _list.push({ value, type: "add", target })
                added += 1
            }
        })

        if (added === 0) {
            this.notify("maxBetError")
            sound.play("blocked", 0.3)
            return
        }

        setStake(_stake)
        setList(_list)
        sound.play("sound", 0.2)
    }

    /* Win / lose state for a spot after the round. Only "lose" folds/hides the
       placed chip.
       For main this covers every main / split box: the chip stays visible if
       at least one box won OR pushed (net >= 0) — e.g. one split hand loses but
       the other draws. It folds only when every box lost. */
    _state = target => {
        const { result } = this.props
        if (!result) return ""
        if (target === "main") {
            const positions = result.positions || []
            if (positions.length === 0) return ""
            const kept = positions.some(pos => (parseFloat(pos.net) || 0) >= 0)
            return kept ? "win" : "lose"
        }
        const side = result.sideResults && result.sideResults[target]
        if (!side) return ""
        return parseFloat(side.sum) > 0 ? "win" : "lose"
    }

    /* Flying chips animation (add / remove) */
    _animation = target => {
        const { list } = this.props
        return (
            <div className="animated-chips">
                {list.map((chip, index) => {
                    if (chip.target !== target) return null
                    return (
                        <div className={`animated-chip ${chip.type === "add" ? "visible" : "hidden"}`} key={`${index}`}>
                            <Chip3D size={this._chipSize(target === "main")} chips={this._chipValues()} value={chip.value} />
                        </div>
                    )
                })}
            </div>
        )
    }

    /* Win multiplier + payout chip */
    _winRate = target => {
        const { result, stake } = this.props
        if (!result) return null

        let multiplier = 0
        let sum = 0

        if (target === "main") {
            /* Winning chip on top = total winnings of the boxes that WON only.
               Losing / pushed boxes contribute nothing (their negative / zero net
               is ignored). So with a 100 + 100 split: one box wins -> 100, both
               win -> 200, both lost or pushed -> no chip.
               (net of a won box = its profit, e.g. 100 stake win -> net 100.) */
            const positions = result.positions || []
            const winSum = positions.reduce((s, pos) => {
                const net = parseFloat(pos.net) || 0
                return s + (net > 0 ? net : 0)
            }, 0)
            if (winSum > 0) {
                const winner = positions.find(pos => parseFloat(pos.net) > 0)
                multiplier = winner ? winner.multiplier : 1
                sum = winSum
            }
        }
        else {
            const r = result.sideResults && result.sideResults[target]
            if (r && parseFloat(r.sum) > 0) { multiplier = r.multiplier; sum = parseFloat(r.sum) - (parseFloat(stake[target]) || 0) }
        }

        if (multiplier > 0 && sum > 0) {
            return (
                <div className="rate-win">
                    <div className="rate-win-text">{multiplier}x</div>
                    <div className="rate-win-chip"><Chip3D size={this._chipSize(target === "main")} chips={this._chipValues()} value={sum} /></div>
                </div>
            )
        }
        return null
    }

    /* Insurance win chip on the main spot (2 x insurance), like a normal win */
    _insuranceWin = () => {
        const { insuranceWin } = this.props
        if (!insuranceWin || insuranceWin <= 0) return null
        return (
            <div className="rate-win">
                <div className="rate-win-text">2x</div>
                <div className="rate-win-chip"><Chip3D size={this._chipSize(true)} chips={this._chipValues()} value={insuranceWin} /></div>
            </div>
        )
    }

    /* One bet spot */
    _rate = target => {

        const { status, stake, doubleMain, insuranceOn, mainStake, result } = this.props
        const state = this._state(target)
        const isMain = target === "main"

        /* Ante chip:
           - After the round is settled -> only the stake still on the table
             remains, i.e. the sum of the boxes that won or pushed (net >= 0).
             A lost box's stake is removed (split 200 -> one box lost, one push
             -> shows 100).
           - During play -> the committed main (mainStake from the server, which
             already reflects double / split; falls back to the raw main bet with
             the optimistic doubleMain flag before the round is committed) plus
             the taken insurance (+half ante). */
        let value = stake[target] || 0
        if (isMain) {
            const base = parseFloat(stake.main) || 0
            if (result) {
                const positions = result.positions || []
                value = positions.reduce((sum, pos) => sum + ((parseFloat(pos.net) || 0) >= 0 ? (parseFloat(pos.bet) || 0) : 0), 0)
            }
            else {
                const committed = parseFloat(mainStake) || 0
                value = committed > 0 ? committed : (doubleMain ? base * 2 : base)
                if (insuranceOn) value = value + base / 2
            }
        }

        /* Arc layout: BET (main) centred & enlarged, side bets raised */
        let style = isMain ? "main-rate" : "additional-rate"
        if (target === "bustBonus" || target === "crazySeven") style = "additional-rate-last"

        const isHidden = state === "lose"

        /* Side bets stay locked (dimmed) until a main BET is placed */
        const locked = status === CHOICE && target !== "main" && !(parseFloat(stake.main) > 0)

        return (
            <div className={`game-rate-block ${style} ${locked ? "disabled" : ""}`}>
                <div onClick={() => this.add(target)} className="game-rate">
                    {isMain
                        ? this._betRing()
                        : (this._icon(target) || <div className="game-rate-text" style={{ fontSize: SIZES[target] }}>{LABELS[target]}</div>)
                    }
                    {value > 0 &&
                        <div className={`game-rate-chip-box ${isMain ? "is-main" : ""} ${isHidden ? "fold" : ""}`}>
                            <Chip3D size={this._chipSize(isMain)} chips={this._chipValues()} value={value} />
                        </div>
                    }
                    {this._animation(target)}
                    {this._winRate(target)}
                    {isMain && this._insuranceWin()}

                    {/* Name caption under the spot — the chip covers the label,
                        so this fades up to show where the bet was placed */}
                    {value > 0 && !isHidden &&
                        <div className="game-rate-name">{LABELS[target]}</div>
                    }
                </div>
            </div>
        )
    }

    render = () => {

        const { status, stake } = this.props

        const mainEmpty = !(parseFloat(stake.main) > 0)

        return (
            <div className={`rates ${status === CHOICE ? "rates-choice" : ""}`}>
                {status === CHOICE &&
                    <div
                        className={`all-side-bets ${mainEmpty ? "disabled" : ""}`}
                        onClick={() => this._addAll()}
                    >
                        ALL SIDE BETS
                    </div>
                }
                {this._rate("bustBonus")}
                {this._rate("twentyone")}
                {this._rate("main")}
                {this._rate("perfectPair")}
                {this._rate("crazySeven")}
            </div>
        )
    }
}

export default Bets
